import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowLeft, Edit, Trash2, Calendar as CalendarIcon } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function TimeEntryManagement() {
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [dateRange, setDateRange] = useState({ start: null, end: null });
  const [formData, setFormData] = useState({
    clock_in_date: "",
    clock_in_time: "",
    clock_out_date: "",
    clock_out_time: ""
  });

  useEffect(() => {
    const userData = localStorage.getItem("user");
    if (!userData || JSON.parse(userData).type !== "admin") {
      navigate("/admin");
      return;
    }
    loadEntries();
  }, [navigate, dateRange]);

  const loadEntries = async () => {
    try {
      const params = {};
      if (dateRange.start && dateRange.end) {
        params.start_date = format(dateRange.start, "yyyy-MM-dd");
        params.end_date = format(dateRange.end, "yyyy-MM-dd");
      }
      const response = await axios.get(`${API}/time-entries`, { params });
      setEntries(response.data);
    } catch (error) {
      console.error("Error loading entries:", error);
    }
  };

  const handleEdit = (entry) => {
    setEditingEntry(entry);
    const clockIn = new Date(entry.clock_in);
    const clockOut = entry.clock_out ? new Date(entry.clock_out) : null;
    
    setFormData({
      clock_in_date: format(clockIn, "yyyy-MM-dd"),
      clock_in_time: format(clockIn, "HH:mm"),
      clock_out_date: clockOut ? format(clockOut, "yyyy-MM-dd") : "",
      clock_out_time: clockOut ? format(clockOut, "HH:mm") : ""
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const clockIn = new Date(`${formData.clock_in_date}T${formData.clock_in_time}`);
      let clockOut = null;
      if (formData.clock_out_date && formData.clock_out_time) {
        clockOut = new Date(`${formData.clock_out_date}T${formData.clock_out_time}`);
      }

      await axios.put(`${API}/time-entries/${editingEntry.id}`, {
        clock_in: clockIn.toISOString(),
        clock_out: clockOut ? clockOut.toISOString() : null
      });
      
      toast.success("Eintrag aktualisiert");
      setDialogOpen(false);
      setEditingEntry(null);
      loadEntries();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Speichern");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Möchten Sie diesen Eintrag wirklich löschen?")) return;
    
    try {
      await axios.delete(`${API}/time-entries/${id}`);
      toast.success("Eintrag gelöscht");
      loadEntries();
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  const formatDuration = (minutes) => {
    if (!minutes) return "-";
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-4 mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate("/admin/dashboard")}
            className="rounded-full"
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <h1 className="text-4xl font-bold text-gray-800" style={{ fontFamily: 'Work Sans, sans-serif' }}>
            Zeiteinträge
          </h1>
        </div>

        <Card className="shadow-xl rounded-3xl border-0 mb-6">
          <CardContent className="p-6">
            <div className="flex gap-4 items-center flex-wrap">
              <div className="flex-1 min-w-[200px]">
                <Label>Zeitraum filtern</Label>
                <div className="flex gap-2 mt-2">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="rounded-full" data-testid="start-date-picker">
                        <CalendarIcon className="w-4 h-4 mr-2" />
                        {dateRange.start ? format(dateRange.start, "dd.MM.yyyy") : "Von"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={dateRange.start}
                        onSelect={(date) => setDateRange({ ...dateRange, start: date })}
                        locale={de}
                      />
                    </PopoverContent>
                  </Popover>
                  
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="rounded-full" data-testid="end-date-picker">
                        <CalendarIcon className="w-4 h-4 mr-2" />
                        {dateRange.end ? format(dateRange.end, "dd.MM.yyyy") : "Bis"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={dateRange.end}
                        onSelect={(date) => setDateRange({ ...dateRange, end: date })}
                        locale={de}
                      />
                    </PopoverContent>
                  </Popover>
                  
                  <Button
                    variant="ghost"
                    onClick={() => setDateRange({ start: null, end: null })}
                    className="rounded-full"
                    data-testid="clear-filter-button"
                  >
                    Zurücksetzen
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid gap-4">
          {entries.length === 0 ? (
            <Card className="shadow-xl rounded-3xl border-0">
              <CardContent className="p-12 text-center">
                <p className="text-gray-500">Keine Einträge gefunden</p>
              </CardContent>
            </Card>
          ) : (
            entries.map((entry) => (
              <Card key={entry.id} className="shadow-lg rounded-3xl border-0" data-testid={`entry-card-${entry.id}`}>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className="text-xl font-bold mb-1">{entry.employee_name}</h3>
                      <p className="text-gray-600">
                        {format(new Date(entry.date), "dd.MM.yyyy", { locale: de })} • 
                        {format(new Date(entry.clock_in), "HH:mm", { locale: de })} - 
                        {entry.clock_out ? format(new Date(entry.clock_out), "HH:mm", { locale: de }) : " Offen"}
                      </p>
                      <p className="text-lg font-semibold text-indigo-600 mt-1">
                        {formatDuration(entry.duration_minutes)}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(entry)}
                        className="rounded-full"
                        data-testid={`edit-${entry.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDelete(entry.id)}
                        className="rounded-full"
                        data-testid={`delete-${entry.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-md rounded-3xl">
            <DialogHeader>
              <DialogTitle>Zeiteintrag bearbeiten</DialogTitle>
            </DialogHeader>
            {editingEntry && (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label>Mitarbeiter</Label>
                  <Input value={editingEntry.employee_name} disabled className="rounded-2xl" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="clock_in_date">Startdatum</Label>
                    <Input
                      id="clock_in_date"
                      type="date"
                      value={formData.clock_in_date}
                      onChange={(e) => setFormData({ ...formData, clock_in_date: e.target.value })}
                      required
                      className="rounded-2xl"
                      data-testid="clock-in-date-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="clock_in_time">Startzeit</Label>
                    <Input
                      id="clock_in_time"
                      type="time"
                      value={formData.clock_in_time}
                      onChange={(e) => setFormData({ ...formData, clock_in_time: e.target.value })}
                      required
                      className="rounded-2xl"
                      data-testid="clock-in-time-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="clock_out_date">Enddatum</Label>
                    <Input
                      id="clock_out_date"
                      type="date"
                      value={formData.clock_out_date}
                      onChange={(e) => setFormData({ ...formData, clock_out_date: e.target.value })}
                      className="rounded-2xl"
                      data-testid="clock-out-date-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="clock_out_time">Endzeit</Label>
                    <Input
                      id="clock_out_time"
                      type="time"
                      value={formData.clock_out_time}
                      onChange={(e) => setFormData({ ...formData, clock_out_time: e.target.value })}
                      className="rounded-2xl"
                      data-testid="clock-out-time-input"
                    />
                  </div>
                </div>
                <Button type="submit" className="w-full rounded-full" data-testid="save-entry-button">
                  Aktualisieren
                </Button>
              </form>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
